// react
import { SyntheticEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// types
import {
	Act,
	ActionButton,
	Contract,
	Document,
	Entity,
	Loan,
	Pagination,
	Transaction,
	User,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import {
	Link as RouterLink,
	Outlet,
	useLocation,
	useParams,
	useSearchParams,
	useNavigate,
} from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextProject, ContextUserProjectPermissions } from '../Project/Project.context';
import {
	ContextSupplier,
	ContextSupplierActs,
	ContextSupplierContracts,
	ContextSupplierDocuments,
	ContextSupplierPayments,
	ContextSupplierUsers,
} from './Supplier.context';

// mui
import { Grid } from '@mui/material';

// pages
import { Details } from './Details/Details';

// dialogs
import { DialogAddAct } from '../../dialogs/DialogAddAct/DialogAddAct';
import { DialogAddContract } from '../../dialogs/DialogAddContract/DialogAddContract';
import { DialogAddTransaction } from '../../dialogs/DialogAddTransaction/DialogAddTransaction';
import { DialogAddUser } from '../../dialogs/DialogAddUser/DialogAddUser';
import { DialogUploadDocs } from '../../dialogs/DialogUploadDocs/DialogUploadDocs';

// ui
import { Breadcrumbs } from '../../ui/Breadcrumbs/Breadcrumbs';
import { Empty } from '../../ui/Empty/Empty';
import { Loading } from '../../ui/Loading/Loading';
import { Tabs } from '../../ui/Tabs/Tabs';
import { Title } from '../../ui/Title/Title';

// defaults
import { defaultPagination } from '../../../utils/defaults';

// config
import { featureFlags } from '../../../config/featureFlags';

export const PageSupplier = () => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const location = useLocation();
	const navigate = useNavigate();
	const { id, idSupplier } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramContract = searchParams.get('contract');
	const paramDialog = searchParams.get('dialog');
	const paramExpenseType = searchParams.get('expenseType');
	const paramLoan = searchParams.get('loan');
	const paramPage = Number(searchParams.get('page') || 1);
	const paramQ = searchParams.get('q');
	const paramStatus = searchParams.get('status');
	const paramSupplier = searchParams.get('supplier');
	const paramType = searchParams.get('type');

	// context
	const { project } = useContext(ContextProject);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	const tabs = useMemo(() => {
		const tabs = [];

		if (userProjectPermissions?.payment?.read) {
			tabs.push({
				component: RouterLink,
				label: t('payments'),
				to: `/project/${id}/supplier/${idSupplier}/payments`,
				value: 'payments',
			});
		}

		if (userProjectPermissions?.user?.read) {
			tabs.push({
				component: RouterLink,
				label: t('users'),
				to: `/project/${id}/supplier/${idSupplier}/users`,
				value: 'users',
			});
		}

		if (userProjectPermissions?.contract?.read) {
			tabs.push({
				component: RouterLink,
				label: t('contracts'),
				to: `/project/${id}/supplier/${idSupplier}/contracts`,
				value: 'contracts',
			});
		}

		if (userProjectPermissions?.document?.read) {
			tabs.push({
				component: RouterLink,
				label: t('documents'),
				to: `/project/${id}/supplier/${idSupplier}/documents`,
				value: 'documents',
			});
		}

		if (userProjectPermissions?.act?.read && featureFlags['ACTIVITIES']) {
			tabs.push({
				component: RouterLink,
				label: t('activities'),
				to: `/project/${id}/supplier/${idSupplier}/activities`,
				value: 'activities',
			});
		}

		return tabs;
	}, [id, idSupplier, t, userProjectPermissions]);

	// defaults
	const getTabByLocation = useCallback(
		(pathname) => {
			if (pathname.includes('activities')) {
				return 'activities';
			} else if (pathname.includes('contracts')) {
				return 'contracts';
			} else if (pathname.includes('documents')) {
				return 'documents';
			} else if (pathname.includes('payments')) {
				return 'payments';
			} else if (pathname.includes('users')) {
				return 'users';
			} else {
				return tabs?.[0]?.value;
			}
		},
		[tabs]
	);

	const defaultTabValue = useMemo(() => {
		return getTabByLocation(location.pathname);
	}, [getTabByLocation, location]);

	// state
	const [acts, setActs] = useState<Act[]>([]);
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [documents, setDocuments] = useState<Document[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [paginationActs, setPaginationActs] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationContracts, setPaginationContracts] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationPayments, setPaginationPayments] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [paginationUsers, setPaginationUsers] = useState<Pagination>({
		...defaultPagination,
		page: paramPage,
	});
	const [payments, setPayments] = useState<Transaction[]>([]);
	const [supplier, setSupplier] = useState<Entity | null>(null);
	const [tabValue, setTabValue] = useState(defaultTabValue);
	const [users, setUsers] = useState<User[]>([]);

	// fetch supplier
	const { isLoading: isLoadingSupplier, refetch: refetchSupplier } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/entities/${idSupplier}?project=${project?.id}&fields=id,idExternal,loans,metadata,name,type,wallet&populate=wallet`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setSupplier(res.data[0]);
			}
		},
	});

	// fetch supplier traceability
	const { fetchRequest: fetchTraceability, isLoading: isLoadingTraceability } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/entities/${idSupplier}?project=${project?.id}&fields=traceability`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]?.traceability) {
				setSupplier((supplierOld) =>
					supplierOld ? { ...supplierOld, traceability: res.data[0].traceability } : null
				);
			}
		},
	});

	// fetch supplier contracts
	const {
		fetchRequest: fetchContracts,
		isLoading: isLoadingContracts,
		refetch: refetchContracts,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/contracts?entities=${idSupplier}&populate=entities&page=${paramPage}&limit=${paginationContracts.perPage}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}

			if (res.pagination) {
				setPaginationContracts(res.pagination);
			}
		},
	});

	// fetch supplier loans
	const { isLoading: isLoadingLoans } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/loans?entity=${idSupplier}&fields=id,code,entities&populate=entities`,

		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res) {
				setLoans(res.data);
			}
		},
	});

	// fetch supplier payments
	let fetchPaymentsUrl = `${
		process.env.REACT_APP_API_URL
	}/transactions?toEntity=${idSupplier}&type=${
		paramType || (featureFlags['DIRECT'] ? 'DIRECT,PAYMENT' : 'PAYMENT')
	}&page=${paramPage}&limit=${
		paginationUsers.perPage
	}&populate=category,component,contract,expenseType,fromAccount,fromEntity,payments.conversions,payments.loan`;

	if (paramExpenseType) {
		fetchPaymentsUrl += `&expenseType=${paramExpenseType}`;
	}

	if (paramContract) {
		fetchPaymentsUrl += `&contract=${paramContract}`;
	}

	if (paramSupplier) {
		fetchPaymentsUrl += `&supplier=${paramSupplier}`;
	}

	if (paramStatus) {
		fetchPaymentsUrl += `&status=${paramStatus}`;
	}

	if (paramLoan) {
		fetchPaymentsUrl += `&loan=${paramLoan}`;
	}

	if (paramQ) {
		fetchPaymentsUrl += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchPayments,
		isLoading: isLoadingPayments,
		refetch: refetchPayments,
	} = useFetch({
		isLazy: true,
		url: fetchPaymentsUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setPayments(res.data);
			}

			if (res.pagination) {
				setPaginationPayments(res.pagination);
			}
		},
	});

	// fetch supplier users
	const userFields =
		'id,firstName,lastName,title,email,phone,country,projects,entities,modified,created';
	let urlUsers = `${process.env.REACT_APP_API_URL}/users?entities=${idSupplier}&fields=${userFields}&populate=country,entities&page=${paramPage}&limit=${paginationUsers.perPage}`;

	if (paramQ) {
		urlUsers += `&q=${paramQ}`;
	}

	const {
		fetchRequest: fetchUsers,
		isLoading: isLoadingUsers,
		refetch: refetchUsers,
	} = useFetch({
		isLazy: true,
		url: urlUsers,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setUsers(res.data);
			}

			if (res.pagination) {
				setPaginationUsers(res.pagination);
			}
		},
	});

	// fetch supplier documents
	const {
		fetchRequest: fetchDocuments,
		isLoading: isLoadingDocuments,
		refetch: refetchDocuments,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/documents?project=${project?.id}&entities=${supplier?.id}&page=${paramPage}&limit=${paginationDocuments.perPage}&fields=contract,created,entities,notarizationHash,id,name,transaction,type,user&populate=contract,entity,transaction,type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocuments(res.data);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// fetch supplier acts
	const {
		fetchRequest: fetchActs,
		isLoading: isLoadingActs,
		refetch: refetchActs,
	} = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/acts?entities=${supplier?.id}&page=${paramPage}&limit=${paginationActs.perPage}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setActs(res.data);
			}

			if (res.pagination) {
				setPaginationActs(res.pagination);
			}
		},
	});

	// vars
	const titleActions: ActionButton[] = [];

	if (tabValue === 'activities' && userProjectPermissions?.act?.create) {
		titleActions.push({
			children: t('addActivity'),
			onClick: () => {
				searchParams.set('dialog', 'addActivity');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}
	if (tabValue === 'users' && userProjectPermissions?.user?.create) {
		titleActions.push({
			children: t('addUser'),
			onClick: () => {
				searchParams.set('dialog', 'addUser');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}
	if (tabValue === 'payments' && userProjectPermissions?.payment?.create) {
		titleActions.push({
			children: t('recordPayment'),
			onClick: () => {
				searchParams.set('dialog', 'recordPayment');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}
	if (tabValue === 'contracts' && userProjectPermissions?.contract?.create) {
		titleActions.push({
			children: t('addContract'),
			onClick: () => {
				searchParams.set('dialog', 'addContract');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}
	if (tabValue === 'documents' && userProjectPermissions?.document?.create) {
		titleActions.push({
			children: t('uploadDocuments'),
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsSupplier');
				setSearchParams(searchParams);
			},
			variant: 'contained',
		});
	}

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		setSearchParams(searchParams);
	};

	let template;

	if (isLoadingSupplier) {
		template = <Loading variant="page" />;
	} else {
		if (supplier) {
			template = (
				<>
					<Grid container spacing={2}>
						<Grid item xs={11} lg={12}>
							<Breadcrumbs
								crumbs={[
									{
										children: t('projects'),
										to: '/projects',
									},
									{
										children: project?.idWbg,
										to: `/project/${id}`,
									},
									{
										children: t('suppliers'),
										to: `/project/${id}/suppliers`,
									},
								]}
								page={supplier.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Title
								actions={titleActions}
								ChipStatusProps={{
									status: supplier?.wallet?.status || 'ACTIVE',
								}}
								color="magenta"
								IconCircleProps={{
									type: 'supplier',
								}}
								id={supplier.idExternal}
								overline={t('supplier')}
								title={supplier.name}
							/>
						</Grid>
						<Grid item xs={12}>
							<Details
								isLoadingLoans={isLoadingLoans}
								isLoadingTraceability={isLoadingTraceability}
								loans={loans}
								supplier={supplier}
							/>
						</Grid>
						<Grid item xs={12}>
							<Tabs onChange={handleChangeTab} tabs={tabs} value={tabValue} hasBorder={false} />
						</Grid>
						<Grid item xs={12}>
							<Outlet />
						</Grid>
					</Grid>
					<DialogAddAct
						isOpen={paramDialog === 'addActivity'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchActs();
						}}
					/>
					<DialogAddUser
						entity={supplier}
						isOpen={paramDialog === 'addUser'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchUsers();
						}}
					/>
					<DialogAddTransaction
						isOpen={paramDialog === 'recordPayment'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchPayments();
						}}
					/>
					<DialogAddContract
						entity={supplier}
						isOpen={paramDialog === 'addContract'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchContracts();
						}}
					/>
					<DialogUploadDocs
						entity={supplier}
						isOpen={paramDialog === 'uploadDocsSupplier'}
						onClose={handleCloseDialog}
						onSuccess={() => {
							refetchDocuments();
						}}
					/>
				</>
			);
		} else {
			template = (
				<Empty
					actions={[
						{
							children: t('suppliers'),
							component: RouterLink,
							to: `/project/${project?.id}/suppliers`,
							variant: 'contained',
						},
					]}
					content={{ children: t('empty.supplier.content') }}
					title={{ children: t('empty.supplier.title') }}
					IconCircleProps={{ type: 'supplier' }}
					variant="page"
				/>
			);
		}
	}

	// fetch traceability when supplier loads
	useEffect(() => {
		if (supplier?.id) {
			fetchTraceability();
		}
	}, [fetchTraceability, supplier?.id]);

	// fetch data when tab changes
	useEffect(() => {
		if (supplier?.id) {
			if (tabValue === 'activities') {
				fetchActs();
			} else if (tabValue === 'contracts') {
				fetchContracts();
			} else if (tabValue === 'documents') {
				fetchDocuments();
			} else if (tabValue === 'payments') {
				fetchPayments();
			} else if (tabValue === 'users') {
				fetchUsers();
			}
		}
	}, [
		fetchActs,
		fetchContracts,
		fetchDocuments,
		fetchPayments,
		fetchUsers,
		paramContract,
		paramExpenseType,
		paramLoan,
		paramPage,
		paramQ,
		paramStatus,
		paramSupplier,
		paramType,
		project,
		supplier,
		tabValue,
	]);

	// set active tab when location changes
	useEffect(() => {
		const tabValueByLocation = getTabByLocation(location.pathname);
		setTabValue(tabValueByLocation);
	}, [getTabByLocation, location]);

	// navigate to first tab if path is not set
	useEffect(() => {
		const segments = location.pathname.split('/');
		if (segments.length < 6) {
			navigate(tabs[0].to);
		}
	}, [location, navigate, tabs]);

	return (
		<ContextSupplier.Provider value={{ isLoadingSupplier, refetchSupplier, setSupplier, supplier }}>
			<ContextSupplierActs.Provider
				value={{
					acts,
					isLoadingActs,
					paginationActs,
					refetchActs,
					setActs,
					setPaginationActs,
				}}>
				<ContextSupplierContracts.Provider
					value={{
						contracts,
						isLoadingContracts,
						paginationContracts,
						refetchContracts,
						setContracts,
						setPaginationContracts,
					}}>
					<ContextSupplierDocuments.Provider
						value={{
							documents,
							isLoadingDocuments,
							paginationDocuments,
							refetchDocuments,
							setDocuments,
							setPaginationDocuments,
						}}>
						<ContextSupplierPayments.Provider
							value={{
								isLoadingPayments,
								paginationPayments,
								payments,
								refetchPayments,
								setPayments,
								setPaginationPayments,
							}}>
							<ContextSupplierUsers.Provider
								value={{
									isLoadingUsers,
									paginationUsers,
									refetchUsers,
									setUsers,
									setPaginationUsers,
									users,
								}}>
								{template}
							</ContextSupplierUsers.Provider>
						</ContextSupplierPayments.Provider>
					</ContextSupplierDocuments.Provider>
				</ContextSupplierContracts.Provider>
			</ContextSupplierActs.Provider>
		</ContextSupplier.Provider>
	);
};
